// Push-notification gating for mention-aware delivery.
//
// One pure-logic function, `route(...)`, that the Service Worker notification
// handler and the in-app foreground-notification path both call to decide what
// UI to surface for an incoming message. Because it is a pure function over
// its inputs, the self-test suite can drive every branch without involving the
// Push API, the Notification API, or the network.
//
// Maps the per-thread `NotificationState` enum onto a Signal-grade decision tree:
//
//   'all'     →  banner on every message
//   'mention' →  banner only if I'm targeted (direct, role, @everyone, @here)
//                otherwise suppress entirely
//   'muted'   →  no banner ever; if I'm targeted, deliver as silent badge
//                so the unread feed still updates (matches Signal's
//                "muted thread but mention-bypass" behaviour)
//   'none'    →  suppress, no badge — full silence
//
// Two pieces of behaviour worth calling out:
//
//   1. Self-authored messages always suppress, even if the body literally
//      contains <@me>. Notifying yourself for your own send is a UX bug.
//   2. @everyone / @here only count as "I'm mentioned" if the channel
//      `allowEveryoneMention` flag is true. This mirrors Discord/Revolt's
//      per-server permission and keeps a single hostile member from
//      pinging every member in a large server who has 'mention' set.
//
// Mirror of `kaether_for_ios/Stoat/E2EE/MentionRouter.swift`.

import { parseMentions, unionUserIds } from "./MentionParser";

// MARK: - Input types

/** Per-thread notification delivery mode. Mirrors iOS `NotificationState`. */
export type NotificationState = "all" | "mention" | "muted" | "none";

export interface ParsedMessage {
  readonly authorId: string;
  readonly body: string;
  /** From Message.mentions; may be empty/absent in E2EE context. */
  readonly serverProvidedMentions: readonly string[];
}

export interface CurrentUserContext {
  readonly userId: string;
  readonly roleIds: ReadonlySet<string>;
}

export interface ChannelNotificationContext {
  readonly state: NotificationState;
  /** Signal-style snooze; null = not snoozed. Epoch milliseconds. */
  readonly mutedUntil: number | null;
  /** Server-level permission gate for @everyone / @here. */
  readonly allowEveryoneMention: boolean;
  readonly isDirectMessage: boolean;
}

// MARK: - Output types

/** Highest-priority mention type the message carries for this user. */
export type MentionTarget =
  | { kind: "directMention" }
  | { kind: "roleMention"; roleId: string }
  | { kind: "everyoneMention" }
  | { kind: "hereMention" };

/** Reason a notification was suppressed. */
export type SuppressReason =
  | "authoredBySelf"
  | "stateNone"
  | "stateMentionRequiresMention"
  | "stateMutedNoMention";

/** What UI a single incoming message should produce. */
export type NotificationDecision =
  /** Full banner; target=null means .all-mode unmentioned banner. */
  | { kind: "banner"; target: MentionTarget | null }
  /** Badge-only delivery (muted-thread mention bypass / snooze downgrade). */
  | { kind: "silent"; target: MentionTarget }
  | { kind: "suppress"; reason: SuppressReason };

// MARK: - Public API

/**
 * Decide what UI a single incoming message should produce.
 *
 * Pure function — no side effects, no I/O.
 *
 * @param message  The decoded (post-decrypt) message envelope.
 * @param user     The currently-authenticated user's ID + role memberships.
 * @param channel  Per-thread notification settings.
 * @param nowMs    Current epoch milliseconds (injectable for testing).
 */
export function route(
  message: ParsedMessage,
  user: CurrentUserContext,
  channel: ChannelNotificationContext,
  nowMs: number = Date.now(),
): NotificationDecision {
  if (message.authorId === user.userId) {
    return { kind: "suppress", reason: "authoredBySelf" };
  }

  const target = resolveTarget(message, user, channel);

  // Snooze: an active snooze window downgrades any state that would
  // banner into the equivalent 'muted' semantics. Mentions still
  // bypass as a silent badge; everything else suppresses. This way
  // there is exactly one place that defines "muted-with-mention-bypass"
  // behaviour and snooze is a thin UI affordance on top.
  const effectiveState: NotificationState = isSnoozed(channel, nowMs)
    ? "muted"
    : channel.state;

  switch (effectiveState) {
    case "none":
      return { kind: "suppress", reason: "stateNone" };

    case "muted":
      if (target !== null) {
        return { kind: "silent", target };
      }
      return { kind: "suppress", reason: "stateMutedNoMention" };

    case "mention":
      if (target !== null) {
        return { kind: "banner", target };
      }
      return { kind: "suppress", reason: "stateMentionRequiresMention" };

    case "all":
      return { kind: "banner", target };
  }
}

// MARK: - Target resolution

/**
 * Pick the highest-priority mention target the message carries for this user,
 * or null if none. Order is direct > role > @here > @everyone so the UI can
 * display the most relevant reason in a single line.
 */
export function resolveTarget(
  message: ParsedMessage,
  user: CurrentUserContext,
  channel: ChannelNotificationContext,
): MentionTarget | null {
  const parsed = parseMentions(message.body);

  const allUserMentions = unionUserIds(message.serverProvidedMentions, parsed);
  if (allUserMentions.has(user.userId)) {
    return { kind: "directMention" };
  }

  for (const roleId of parsed.roleIds) {
    if (user.roleIds.has(roleId)) {
      return { kind: "roleMention", roleId };
    }
  }

  // @here is more specific than @everyone, so prefer it when both appear
  // (routing-wise @here is more targeted and should win).
  if (channel.allowEveryoneMention) {
    if (parsed.here) {
      return { kind: "hereMention" };
    }
    if (parsed.everyone) {
      return { kind: "everyoneMention" };
    }
  }

  return null;
}

// MARK: - Snooze window

function isSnoozed(
  channel: ChannelNotificationContext,
  nowMs: number,
): boolean {
  if (channel.mutedUntil === null) {
    return false;
  }
  return nowMs < channel.mutedUntil;
}
